import { createApp } from "vue";

import App from "./App.vue";

import { registerStore } from "./store";

import { registerRouter } from "./router";

import registerDirective from "@/utils/directives";

import { loading as registerLoading } from "@/utils/register";

import registerI18n from "@/plugins/i18n";

import "@/plugins/loglevel/renderer";

import "@/plugins/lib-flexible";

import { createKeythereum } from "@/plugins/keythereum";

import "animate.css";

import "@/plugins/set-global";
import Casdoor from "casdoor-vue-sdk";
import { casdoorConfig } from "@/plugins/casdoor";
// import { init } from "@sentry/electron/renderer";
// import { init as initVue } from "@sentry/vue";
async function initializeSentry(app: any) {
  if (process.env.IS_ELECTRON) {
    const Sentry = await import("@sentry/electron/renderer");
    const SentryVue = await import("@sentry/vue");

    const sentryDsn = process.env.VUE_APP_SENTRY_DSN;
    const sentryEnv =
      process.env.VUE_APP_ENV === "test" ? "test" : "production";
    const sentryRelease =
      process.env.SENTRY_RELEASE ||
      `dianshu@${process.env.VUE_APP_VERSION || "unknown"}`;

    Sentry.init(
      {
        dsn: sentryDsn,
        release: sentryRelease,
        environment: sentryEnv,
        debug: true,
        // 启用结构化日志功能
        _experiments: { enableLogs: true },
        // 启用控制台日志集成
        integrations: [
          Sentry.consoleLoggingIntegration({
            levels: ["log", "warn", "error"],
          }),
          // 启用作用域到主进程的集成，提高性能
          Sentry.scopeToMainIntegration(),
          // 启用事件循环阻塞检测
          Sentry.eventLoopBlockIntegration(),
        ],
        autoSessionTracking: true,
      },
      (options) => {
        SentryVue.init({
          ...options,
          app,
          tracesSampleRate: 1.0,
          attachProps: true,
          release: sentryRelease,
          environment: sentryEnv,
          integrations: [Sentry.replayIntegration() as any],
          // Session Replay
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        });

        // 兜底一次会话启动，防止时序问题导致会话未创建
        try {
          (Sentry as any)?.startSession?.();
          const capSess = (Sentry as any)?.captureSession;
          if (typeof capSess === "function") {
            capSess();
          } else {
            (Sentry as any)?.captureEvent?.({ message: "session-ping" });
          }
        } catch {
          /* ignore */
        }
      }
    );

    // 暴露 Sentry 到全局，供其他模块使用
    (global as any).Sentry = Sentry;
  }
}

if (process.env.IS_ELECTRON && process.env.NODE_ENV === "production") {
  createKeythereum(window.document, "app://./keythereum.js");
} else {
  createKeythereum(window.document, "/keythereum.js");
}

async function bootstrap() {
  console.time("Vue App Initialization");
  global.log.info("Starting Vue app initialization");

  console.time("Create Vue App");
  const app = createApp(App);
  console.timeEnd("Create Vue App");
  global.log.info("Vue app created");

  console.time("Initialize Sentry");
  await initializeSentry(app);
  console.timeEnd("Initialize Sentry");
  global.log.info("Sentry initialized");

  console.time("Initialize Casdoor");
  app.use(Casdoor, casdoorConfig);
  console.timeEnd("Initialize Casdoor");
  global.log.info("Casdoor initialized");

  console.time("Register Store");
  registerStore(app);
  console.timeEnd("Register Store");
  global.log.info("Store registered");

  console.time("Register Router");
  registerRouter(app);
  console.timeEnd("Register Router");
  global.log.info("Router registered");

  console.time("Register Directive");
  registerDirective(app);
  console.timeEnd("Register Directive");
  global.log.info("Directive registered");

  console.time("Register Loading");
  registerLoading();
  console.timeEnd("Register Loading");
  global.log.info("Loading registered");

  console.time("Register i18n");
  registerI18n(app);
  console.timeEnd("Register i18n");
  global.log.info("i18n registered");

  console.time("Mount App");
  app.mount("#app");
  console.timeEnd("Mount App");
  global.log.info("App mounted");

  console.timeEnd("Vue App Initialization");
  global.log.info("Vue app initialization completed");
}

if (process.env.VUE_APP_ENV === "custom") {
  import("@/api/__mock__").then(() => {
    bootstrap();
  });
} else {
  process.env.IS_ELECTRON
    ? bootstrap()
    : import("@/api/__mock__").then(() => {
        bootstrap();
      });
}
